import { ReactElement, useState, useEffect, useRef, useCallback } from "react";
import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import TemplateMenubar from "../templates/components/template-menubar";
import { useGetPlatformsQuery, useGetAccountsByPlatformQuery, useGetCampaignsByAccountQuery, useGetReportMetricsMutation, useGetTimeSeriesDataMutation, useChatMutation, downloadReportFile, useGetBreakdownDataMutation, useGetGeoDataMutation, useGetDomainDataMutation, useGetAppDataMutation } from "../../redux/admin/reporting/reporting.endpoints";
import { useGetClientDetailsQuery } from "../../redux/organization/organization.endpoints";
import { Skeleton } from "src/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import MetricCard from './components/metric-card';
import TableMetricCard from './components/table-metric-card';
import CampaignBreakdownTable from './components/campaign-breakdown-table';
import { ReportMetrics, AccountType, CampaignType, TimeSeriesDataPoint, RawDataPoint, ChatMessage, AIGeneratedItem, AIGeneratedItemType, TableDataPoint } from "src/redux/admin/reporting/reporting.types";
import ErrorLogService from "../../services/error-log/error-log.service";
import ChatInterface from './components/chat-interface';
import MetricChart from "./components/metric-chart";
import './campaign-reporter.styles.css';
import AIGeneratedItems from './components/ai-generated-items';
import { ReactComponent as Download } from "src/assets/icons-redesign/download.svg";
import { ReactComponent as EyeIcon } from "src/assets/icons/eye.svg";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../redux/notification/notification.slice";
import { Loader } from "lucide-react";
import { ReduxState } from "../../redux/redux-state";
import { ClientView, FALLBACK_DEFAULT_CLIENT } from "src/redux/organization/organization.types";
import { AVAILABLE_METRICS } from "src/components/form/update-profile-form/customize-reporter-modal";

const DEFAULT_CHAT_MESSAGE: ChatMessage = { content: "Hi! How would you like to analyze your campaign data?", type: 'ai' };

const CampaignReporterPage = (): ReactElement => {
  useSetPageTitle('Campaign Reporter');
  const dispatch = useDispatch();
  const userState = useSelector((state: ReduxState) => state.userState);
  const isClientView = userState.permissions?.groups?.includes('client_user') || userState.permissions?.groups?.includes('client_admin');
  const urlParams = new URLSearchParams(location.search);
  const previewClientId = urlParams.get('previewClientId');
  const previewClientName = urlParams.get('previewClientName');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();

  const { data: previewClient } = useGetClientDetailsQuery(previewClientId ?? '', {
    skip: !previewClientId,
  });

  const [platform, setPlatform] = useState<string>(urlParams.get("platform") || "");
  const [accountIds, setAccountIds] = useState<number[]>(urlParams.get("accountIds") ? urlParams.get("accountIds")!.split(',').map(Number) : []);
  const [campaignIds, setCampaignIds] = useState<number[]>(urlParams.get("campaignIds") ? urlParams.get("campaignIds")!.split(',').map(Number) : []);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: urlParams.get("startDate") || new Date(new Date().setDate(new Date().getDate() - 7)).toLocaleDateString('en-CA'),
    end: urlParams.get("endDate") || new Date(new Date().setDate(new Date().getDate() - 1)).toLocaleDateString('en-CA'),
  });
  const [pendingPlatform, setPendingPlatform] = useState<string>(platform);
  const [pendingAccountIds, setPendingAccountIds] = useState<number[]>(accountIds);
  const [pendingCampaignIds, setPendingCampaignIds] = useState<number[]>(campaignIds);
  const [pendingDateRange, setPendingDateRange] = useState<{ start: string; end: string }>(dateRange);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [searchAccount, setSearchAccount] = useState("");
  const [searchCampaign, setSearchCampaign] = useState("");
  const [userClearedCampaigns, setUserClearedCampaigns] = useState(false);

  const [reportMetrics, setReportMetrics] = useState<ReportMetrics | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);
  const [performanceData, setPerformanceData] = useState<RawDataPoint[] | null>(null);
  const [geoData, setGeoData] = useState<TableDataPoint[]>([]);
  const [domainData, setDomainData] = useState<TableDataPoint[]>([]);
  const [appData, setAppData] = useState<TableDataPoint[]>([]);

  const [isAccountDropdownVisible, setAccountDropdownVisible] = useState(false);
  const [isCampaignDropdownVisible, setCampaignDropdownVisible] = useState(false);
  const accountDropdownRef = useRef<HTMLDivElement | null>(null);
  const campaignDropdownRef = useRef<HTMLDivElement | null>(null);
  const aiReporterRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([DEFAULT_CHAT_MESSAGE]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [aiGeneratedItems, setAiGeneratedItems] = useState<AIGeneratedItem[]>([]);
  const [activeTab, setActiveTab] = useState<AIGeneratedItemType | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const baseReset = useCallback(() => {
    setReportMetrics(null);
    setTimeSeriesData([]);
    setPerformanceData(null);
    setGeoData([]);
    setDomainData([]);
    setAppData([]);
    setAiGeneratedItems([]);
    setActiveTab(undefined);
    setMessages([DEFAULT_CHAT_MESSAGE]);
    setThreadId(null);
  }, []);

  const { data: platforms = [], isFetching: isFetchingPlatforms } = useGetPlatformsQuery({ previewClientId: previewClientId || undefined });
  const { data: accounts = [], isFetching: isFetchingAccounts, error: accountsError } = useGetAccountsByPlatformQuery({ source: pendingPlatform, previewClientId: previewClientId || undefined }, { skip: !pendingPlatform });
  const { data: campaigns = [], isFetching: isFetchingCampaigns, error: campaignsError } = useGetCampaignsByAccountQuery({ source: pendingPlatform, accountId: pendingAccountIds, previewClientId: previewClientId || undefined }, { skip: pendingAccountIds.length === 0 });
  const [fetchReportMetrics, { data: metricsData, isLoading: isLoadingMetrics, error: metricsError }] = useGetReportMetricsMutation();
  const [fetchTimeSeriesData, { data: rawTimeSeriesData, isLoading: isLoadingTimeSeries, error: timeSeriesError }] = useGetTimeSeriesDataMutation();
  const [fetchBreakdownData, { data: rawPerformanceData, isLoading: isLoadingPerformanceData, error: performanceError }] = useGetBreakdownDataMutation();
  const [fetchGeoData, { data: rawGeoData, isLoading: isLoadingGeoData, error: geoError }] = useGetGeoDataMutation();
  const [fetchDomainData, { data: rawDomainData, isLoading: domainLoading, error: domainError }] = useGetDomainDataMutation();
  const [fetchAppData, { data: rawAppData, isLoading: appLoading, error: appError }] = useGetAppDataMutation();
  const [chat, { isLoading: isChatLoading }] = useChatMutation();

  useEffect(() => {
    accountsError && ErrorLogService.logError(accountsError, 'campaign-reporter.page.tsx', 'useGetAccountsByPlatformQuery');
    campaignsError && ErrorLogService.logError(campaignsError, 'campaign-reporter.page.tsx', 'useGetCampaignsByAccountQuery');
    metricsError && ErrorLogService.logError(metricsError, 'campaign-reporter.page.tsx', 'fetchMetricsData');
    timeSeriesError && ErrorLogService.logError(timeSeriesError, 'campaign-reporter.page.tsx', 'fetchTimeSeriesData');
    geoError && ErrorLogService.logError(geoError, 'campaign-reporter.page.tsx', 'fetchGeoData');
    domainError && ErrorLogService.logError(domainError, 'campaign-reporter.page.tsx', 'fetchDomainData');
    appError && ErrorLogService.logError(appError, 'campaign-reporter.page.tsx', 'fetchAppData');
  }, [accountsError, campaignsError, metricsError, timeSeriesError, geoError, domainError, appError]);

  const fetchData = async() => {
    if (accountIds.length > 0 && campaignIds.length > 0 && dateRange.start && dateRange.end) {
      try {
        await fetchReportMetrics({
          source: platform,
          account_ids: accountIds,
          campaign_ids: campaignIds,
          start_date: dateRange.start,
          end_date: dateRange.end,
          previewClientId: previewClientId || undefined,
        });
      } catch (error) {
        ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'fetchData');
      }
    }
  };

  const fetchTimeSeries = async() => {
    if (accountIds.length > 0 && campaignIds.length > 0 && dateRange.start && dateRange.end) {
      try {
        await fetchTimeSeriesData({
          source: platform,
          account_ids: accountIds,
          campaign_ids: campaignIds,
          start_date: dateRange.start,
          end_date: dateRange.end,
          previewClientId: previewClientId || undefined,
        });
      } catch (error) {
        ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'fetchTimeSeries');
      }
    }
  };

  const fetchBreakdown = async() => {
    if (accountIds.length > 0 && campaignIds.length > 0 && dateRange.start && dateRange.end) {
      try {
        await fetchBreakdownData({
          source: platform,
          account_ids: accountIds,
          campaign_ids: campaignIds,
          start_date: dateRange.start,
          end_date: dateRange.end,
          previewClientId: previewClientId || undefined,
        });
      } catch (error) {
        ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'fetchBreakdown');
      }
    }
  };

  const fetchGeo = async() => {
    if (accountIds.length > 0 && campaignIds.length > 0 && dateRange.start && dateRange.end) {
      try {
        await fetchGeoData({
          source: platform,
          account_ids: accountIds,
          campaign_ids: campaignIds,
          start_date: dateRange.start,
          end_date: dateRange.end,
          previewClientId: previewClientId || undefined,
        });
      } catch (error) {
        ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'fetchGeo');
      }
    }
  };

  const fetchDomain = async() => {
    if (accountIds.length > 0 && campaignIds.length > 0 && dateRange.start && dateRange.end) {
      try {
        await fetchDomainData({
          source: platform,
          account_ids: accountIds,
          campaign_ids: campaignIds,
          start_date: dateRange.start,
          end_date: dateRange.end,
          previewClientId: previewClientId || undefined,
        });
      } catch (error) {
        ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'fetchDomain');
      }
    }
  };

  const fetchApp = async() => {
    if (accountIds.length > 0 && campaignIds.length > 0 && dateRange.start && dateRange.end) {
      try {
        await fetchAppData({
          source: platform,
          account_ids: accountIds,
          campaign_ids: campaignIds,
          start_date: dateRange.start,
          end_date: dateRange.end,
          previewClientId: previewClientId || undefined,
        });
      } catch (error) {
        ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'fetchApp');
      }
    }
  };

  useEffect(() => {
    if (rawTimeSeriesData) {
      const processedData = rawTimeSeriesData.map((point: any) => ({
        ...point,
        video_completion_rate: point.video_starts > 0 ? (point.video_completes / point.video_starts) * 100 : 0,
        audio_completion_rate: point.audio_starts > 0 ? (point.audio_completes / point.audio_starts) * 100 : 0,
      }));
      setTimeSeriesData(processedData as TimeSeriesDataPoint[]);
    }
  }, [rawTimeSeriesData]);

  useEffect(() => {
    setReportMetrics(metricsData || null);
  }, [metricsData]);
  useEffect(() => { // set the breakdown data here
    setPerformanceData(rawPerformanceData || null);
  }, [rawPerformanceData]);
  useEffect(() => {
    setGeoData(rawGeoData || []);
  }, [rawGeoData]);
  useEffect(() => {
    setDomainData(rawDomainData || []);
  }, [rawDomainData]);
  useEffect(() => {
    setAppData(rawAppData || []);
  }, [rawAppData]);

  // Default selection of platform when none is specified (on initial load)
  useEffect(() => {
    if (isInitialLoad && platforms.length > 0 && !platform && !isFetchingPlatforms) {
      setPlatform(platforms[0]);
      setPendingPlatform(platforms[0]);
    }
  }, [platforms, platform, isFetchingPlatforms, isInitialLoad]);

  // Default selection of all accounts when none are specified (on initial load)
  useEffect(() => {
    if (isInitialLoad && accounts.length > 0 && !isFetchingAccounts) {
      const allAccountIds = accounts.map(account => account.id);
      setAccountIds(allAccountIds);
      setPendingAccountIds(allAccountIds);
      setCampaignIds([]);
      setPendingCampaignIds([]);
    }
  }, [isInitialLoad, accounts, isFetchingAccounts]);

  // Default selection of all campaigns when none are specified (on initial load)
  useEffect(() => {
    if (isInitialLoad && campaigns.length > 0 && campaignIds.length === 0 && accountIds.length > 0 && !isFetchingCampaigns) {
      const allCampaignIds = campaigns.map(campaign => campaign.id);
      setCampaignIds(allCampaignIds);
      setPendingCampaignIds(allCampaignIds);
    }
  }, [campaigns, campaignIds.length, accountIds, isFetchingCampaigns, isInitialLoad]);

  // Default selection of all pending campaigns when none are specified (on non-initial loads)
  useEffect(() => {
    if (campaigns.length > 0 && pendingCampaignIds.length === 0 && pendingAccountIds.length > 0 && !isFetchingCampaigns && !userClearedCampaigns) {
      const allCampaignIds = campaigns.map(campaign => campaign.id);
      setPendingCampaignIds(allCampaignIds);
    }
  }, [campaigns, pendingCampaignIds, pendingAccountIds, isFetchingCampaigns, userClearedCampaigns]);

  // Initial data fetch
  useEffect(() => {
    if (isInitialLoad && accountIds.length > 0 && campaignIds.length > 0 && dateRange.start && dateRange.end) {
      fetchData();
      fetchTimeSeries();
      fetchBreakdown();
      fetchGeo();
      fetchDomain();
      fetchApp();
      setIsInitialLoad(false);

      // Set URL params on initial load
      const newUrlParams = new URLSearchParams();
      platform ? newUrlParams.set("platform", platform) : newUrlParams.delete("platform");
      accountIds.length > 0 ? newUrlParams.set("accountIds", accountIds.join(',')) : newUrlParams.delete("accountIds");
      campaignIds.length > 0 ? newUrlParams.set("campaignIds", campaignIds.join(',')) : newUrlParams.delete("campaignIds");
      dateRange.start ? newUrlParams.set("startDate", dateRange.start) : newUrlParams.delete("startDate");
      dateRange.end ? newUrlParams.set("endDate", dateRange.end) : newUrlParams.delete("endDate");
      // Preserve previewClientId and previewClientName if they exist
      if (previewClientId) {
        newUrlParams.set("previewClientId", previewClientId);
      }
      if (previewClientName) {
        newUrlParams.set("previewClientName", previewClientName);
      }
      navigate(`${location.pathname}?${newUrlParams.toString()}`, { replace: true });
    }
  }, [platform, accountIds, campaignIds, dateRange, isInitialLoad]);

  useEffect(() => {
    const hasChanges =
      pendingPlatform !== platform ||
      JSON.stringify(pendingAccountIds) !== JSON.stringify(accountIds) ||
      JSON.stringify(pendingCampaignIds) !== JSON.stringify(campaignIds) ||
      pendingDateRange.start !== dateRange.start ||
      pendingDateRange.end !== dateRange.end;
    setHasPendingChanges(hasChanges);
  }, [pendingPlatform, platform, pendingAccountIds, accountIds, pendingCampaignIds, campaignIds, pendingDateRange, dateRange]);

  const isGenerateDisabled = !pendingPlatform || pendingAccountIds.length === 0 || pendingCampaignIds.length === 0 || !pendingDateRange.start || !pendingDateRange.end;

  const customSort = (a: string, b: string): number => {
    const aStartsWithNumber = /^\d/.test(a);
    const bStartsWithNumber = /^\d/.test(b);
    if (aStartsWithNumber && !bStartsWithNumber) return 1;
    if (!aStartsWithNumber && bStartsWithNumber) return -1;
    return a.localeCompare(b);
  };

  // Filter accounts and campaigns based on search input
  const filteredAccounts = accounts
    .filter((account: AccountType) =>
      account && account.name && account.name?.toLowerCase().includes(searchAccount.toLowerCase())
    )
    .sort((a: AccountType, b: AccountType) =>
      customSort(a.name, b.name)
    );

  const filteredCampaigns = campaigns
    .filter((campaign: CampaignType) =>
      campaign && campaign.name && campaign.name?.toLowerCase().includes(searchCampaign.toLowerCase())
    )
    .sort((a: CampaignType, b: CampaignType) =>
      customSort(a.name, b.name)
    );

  const metricCardClicksTitle = () => {
    if ((reportMetrics?.current?.clicks ?? 0) > 0 && (reportMetrics?.current?.views ?? 0) === 0) return "Clicks";
    else if ((reportMetrics?.current?.clicks ?? 0) === 0 && (reportMetrics?.current?.views ?? 0) > 0) return "Views";
    else return 'Interactions';
  };

  const metricCardClicksValue = (isDif: boolean) => {
    if (isDif) {
      return (reportMetrics?.dif?.clicks ?? 0) + (reportMetrics?.dif?.views ?? 0);
    } else {
      return (reportMetrics?.current?.clicks ?? 0) + (reportMetrics?.current?.views ?? 0);
    }
  };

  const calculateVideoCompletionRate = () => {
    const starts = reportMetrics?.current?.video_starts ?? 0;
    const completes = reportMetrics?.current?.video_completes ?? 0;
    if (starts === 0) return 0;
    return (completes / starts) * 100;
  };

  const calculateAudioCompletionRate = () => {
    const starts = reportMetrics?.current?.audio_starts ?? 0;
    const completes = reportMetrics?.current?.audio_completes ?? 0;
    if (starts === 0) return 0;
    return (completes / starts) * 100;
  };

  const calculateVideoCompletionRateDifference = () => {
    const currentStarts = reportMetrics?.current?.video_starts ?? 0;
    const currentCompletes = reportMetrics?.current?.video_completes ?? 0;
    const difStarts = reportMetrics?.dif?.video_starts ?? 0;
    const difCompletes = reportMetrics?.dif?.video_completes ?? 0;
    const currentRate = currentStarts > 0 ? (currentCompletes / currentStarts) * 100 : 0;

    const previousStarts = currentStarts - difStarts;
    const previousCompletes = currentCompletes - difCompletes;
    const previousRate = previousStarts > 0 ? (previousCompletes / previousStarts) * 100 : 0;

    return currentRate - previousRate;
  };

  const calculateAudioCompletionRateDifference = () => {
    const currentStarts = reportMetrics?.current?.audio_starts ?? 0;
    const currentCompletes = reportMetrics?.current?.audio_completes ?? 0;
    const difStarts = reportMetrics?.dif?.audio_starts ?? 0;
    const difCompletes = reportMetrics?.dif?.audio_completes ?? 0;
    const currentRate = currentStarts > 0 ? (currentCompletes / currentStarts) * 100 : 0;

    const previousStarts = currentStarts - difStarts;
    const previousCompletes = currentCompletes - difCompletes;
    const previousRate = previousStarts > 0 ? (previousCompletes / previousStarts) * 100 : 0;

    return currentRate - previousRate;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setAccountDropdownVisible(false);
        setSearchAccount("");
      }
      if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(event.target as Node)) {
        setCampaignDropdownVisible(false);
        setSearchCampaign("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSendMessage = async(message: string) => {
    setMessages(prev => [...prev, { content: message, type: 'human' }]);
    setActiveTab(undefined);
    try {
      const requestBody = {
        user_input: message,
        source: platform,
        account_ids: accountIds,
        campaign_ids: campaignIds,
        start_date: dateRange.start,
        end_date: dateRange.end,
        ...(threadId && { thread_id: threadId }),
      };
      const response = await chat(requestBody).unwrap();

      // Add only final AI response to the chat
      const lastAiMessage = response.messages
        .filter(msg => msg.type === 'ai')
        .pop();
      if (lastAiMessage) {
        setMessages(prev => [...prev, lastAiMessage]);
      }

      setAiGeneratedItems(prev => {
        const newItems = response.items as AIGeneratedItem[];
        return [...new Set([...prev, ...newItems].map(item => JSON.stringify(item)))].map(str => JSON.parse(str));
      });
      if (response.thread_id) {
        setThreadId(response.thread_id);
      }
      if (response.decision) {
        const decision = response.decision as AIGeneratedItemType;
        setActiveTab(decision);
      }
    } catch (error) {
      ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'handleSendMessage');
      setMessages(prev => [...prev, { content: 'Error: Failed to get response from the AI model.', type: 'ai' }]);
    }
  };

  useEffect(() => {
    if (isChatLoading) {
      setMessages(prev => [...prev, { content: "...", type: 'ai' }]);
    } else {
      setMessages(prev => prev.filter(msg => msg.content !== "..."));
    }
  }, [isChatLoading]);

  const handleDownload = async() => {
    setIsDownloading(true);
    try {
      const blob = await downloadReportFile({
        source: platform,
        account_ids: accountIds,
        campaign_ids: campaignIds,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });

      if (!blob) {
        dispatch(showNotification({
          summary: "There is no data available for the selected criteria.",
          title: "No data available.",
          theme: "warning",
        }));
        return;
      }

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign-report-${platform}-${dateRange.start}-${dateRange.end}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      ErrorLogService.logError(error, 'campaign-reporter.page.tsx', 'handleDownload');
      dispatch(showNotification({
        summary: "Failed to download report. Please try again.",
        title: "Error",
        theme: "warning",
      }));
    } finally {
      setIsDownloading(false);
    }
  };

  const getMaxDate = () => {
    const date = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = () => {
    // Update the actual state and URL params only when Update is clicked
    setPlatform(pendingPlatform);
    setAccountIds(pendingAccountIds);
    setCampaignIds(pendingCampaignIds);
    setDateRange(pendingDateRange);

    const newUrlParams = new URLSearchParams();
    pendingPlatform ? newUrlParams.set("platform", pendingPlatform) : newUrlParams.delete("platform");
    pendingAccountIds.length > 0 ? newUrlParams.set("accountIds", pendingAccountIds.join(',')) : newUrlParams.delete("accountIds");
    pendingCampaignIds.length > 0 ? newUrlParams.set("campaignIds", pendingCampaignIds.join(',')) : newUrlParams.delete("campaignIds");
    pendingDateRange.start ? newUrlParams.set("startDate", pendingDateRange.start) : newUrlParams.delete("startDate");
    pendingDateRange.end ? newUrlParams.set("endDate", pendingDateRange.end) : newUrlParams.delete("endDate");
    // Preserve previewClientId and previewClientName if they exist
    if (previewClientId) {
      newUrlParams.set("previewClientId", previewClientId);
    }
    if (previewClientName) {
      newUrlParams.set("previewClientName", previewClientName);
    }
    navigate(`${location.pathname}?${newUrlParams.toString()}`, { replace: true });

    baseReset();

    fetchReportMetrics({
      source: pendingPlatform,
      account_ids: pendingAccountIds,
      campaign_ids: pendingCampaignIds,
      start_date: pendingDateRange.start,
      end_date: pendingDateRange.end,
      previewClientId: previewClientId || undefined,
    });

    fetchTimeSeriesData({
      source: pendingPlatform,
      account_ids: pendingAccountIds,
      campaign_ids: pendingCampaignIds,
      start_date: pendingDateRange.start,
      end_date: pendingDateRange.end,
      previewClientId: previewClientId || undefined,
    });

    fetchBreakdownData({
      source: pendingPlatform,
      account_ids: pendingAccountIds,
      campaign_ids: pendingCampaignIds,
      start_date: pendingDateRange.start,
      end_date: pendingDateRange.end,
      previewClientId: previewClientId || undefined,
    });

    fetchGeoData({
      source: pendingPlatform,
      account_ids: pendingAccountIds,
      campaign_ids: pendingCampaignIds,
      start_date: pendingDateRange.start,
      end_date: pendingDateRange.end,
      previewClientId: previewClientId || undefined,
    });

    fetchDomainData({
      source: pendingPlatform,
      account_ids: pendingAccountIds,
      campaign_ids: pendingCampaignIds,
      start_date: pendingDateRange.start,
      end_date: pendingDateRange.end,
      previewClientId: previewClientId || undefined,
    });

    fetchAppData({
      source: pendingPlatform,
      account_ids: pendingAccountIds,
      campaign_ids: pendingCampaignIds,
      start_date: pendingDateRange.start,
      end_date: pendingDateRange.end,
      previewClientId: previewClientId || undefined,
    });
  };

  const getCurrentClientView = useCallback(() => {
    if (!accountIds.length) return FALLBACK_DEFAULT_CLIENT;

    // Helper function to find the appropriate view
    const findView = (views: ClientView[] | undefined) => {
      if (!views) return null;

      // If multiple accounts are selected, use client's default view
      if (accountIds.length > 1) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }

      // Find view for current account
      const currentView = views.find((view: ClientView) => view.account_ext_id === accountIds[0].toString());
      const notConfigured = currentView &&
        (!currentView.metrics?.length) &&
        (!currentView.graph_one?.metric_one && !currentView.graph_one?.metric_two) &&
        (!currentView.graph_two?.metric_one && !currentView.graph_two?.metric_two) &&
        (!currentView.graph_three?.metric_one && !currentView.graph_three?.metric_two);

      if (notConfigured) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }
      return currentView;
    };

    // If in preview mode, use the preview client's view configuration
    if (previewClientId && previewClient) {
      return findView(previewClient.views) as ClientView ?? FALLBACK_DEFAULT_CLIENT;
    }

    // Regular client view logic
    if (!isClientView) return FALLBACK_DEFAULT_CLIENT;
    return findView(userState.client?.views) as ClientView ?? FALLBACK_DEFAULT_CLIENT;
  }, [isClientView, accountIds, userState.client?.views, previewClientId, previewClient]);

  // Hide 'zero' & hidden client metrics
  const shouldShowMetric = useCallback((metric: string) => {
    if (isClientView || previewClientId) {
      const currentView = getCurrentClientView();
      const included = currentView?.metrics.includes(metric) ?? false;
      if (!included) return false;
    }

    if (metric === 'geo') {
      return geoData.length > 0 || isLoadingGeoData;
    }
    if (metric === 'domain') {
      return domainData.length > 0 || domainLoading;
    }
    if (metric === 'app') {
      return appData.length > 0 || appLoading;
    }

    if (!reportMetrics?.current) return true;
    if (metric === 'video_completion_rate') {
      return !!(reportMetrics?.current && reportMetrics.current.video_starts > 0);
    }
    if (metric === 'audio_completion_rate') {
      return !!(reportMetrics?.current && reportMetrics.current.audio_starts > 0);
    }
    const value = reportMetrics.current[metric];
    return typeof value !== 'undefined' && value !== 0;
  }, [isClientView, previewClientId, getCurrentClientView, reportMetrics, geoData, domainData, appData, isLoadingGeoData, domainLoading, appLoading]);

  const getGraphMetrics = useCallback((graphNumber: 1 | 2 | 3) => {
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#e65051', '#4a90e2'];
    const hasNonZero = (metric: string) => {
      if (!timeSeriesData || timeSeriesData.length === 0) return false;
      if (metric === 'video_completion_rate') {
        return timeSeriesData.some(point => point.video_starts && point.video_starts > 0);
      }
      if (metric === 'audio_completion_rate') {
        return timeSeriesData.some(point => point.audio_starts && point.audio_starts > 0);
      }
      return timeSeriesData.some(point => point[metric] && point[metric] !== 0);
    };

    // Get the current view configuration
    const currentView = getCurrentClientView();
    if (!currentView) return [];

    const graphKey = `graph_${graphNumber === 1 ? 'one' : graphNumber === 2 ? 'two' : 'three'}` as const;
    const graph = currentView[graphKey];
    if (!graph.metric_one && !graph.metric_two) return [];

    // Offset color index for each graph
    const colorOffset = (graphNumber - 1) * 2;
    const metrics = [];
    if (graph.metric_one && hasNonZero(graph.metric_one)) {
      metrics.push({
        key: graph.metric_one,
        name: graph.metric_one === 'clicks' ? metricCardClicksTitle() : AVAILABLE_METRICS.find(m => m.value === graph.metric_one)?.label || graph.metric_one,
        color: colors[colorOffset],
      });
    }
    if (graph.metric_two && hasNonZero(graph.metric_two)) {
      metrics.push({
        key: graph.metric_two,
        name: graph.metric_two === 'clicks' ? metricCardClicksTitle() : AVAILABLE_METRICS.find(m => m.value === graph.metric_two)?.label || graph.metric_two,
        color: colors[colorOffset + 1],
      });
    }
    return metrics;
  }, [getCurrentClientView, timeSeriesData, metricCardClicksTitle]);

  const getGraphTitle = useCallback((graphNumber: 1 | 2 | 3) => {
    const metrics = getGraphMetrics(graphNumber);
    if (metrics.length === 0) return '';
    return `${metrics.map(m => m.name).join(' & ')} Over Time`;
  }, [getGraphMetrics]);

  const PreviewBanner = () => {
    if (!previewClientId || isClientView) return null;

    const handleExitPreview = () => {
      const params = new URLSearchParams(window.location.search);
      params.delete('previewClientId');
      params.delete('previewClientName');
      window.location.replace(`${location.pathname}?${params.toString()}`);
    };

    return (
      <div className="inline-flex items-center pl-3 pr-1 py-1 rounded-lg bg-blue-100 text-blue-800 font-semibold text-sm border border-blue-300">
        <EyeIcon className="w-4 h-4 mr-1" />
        <span className="-mb-[2px]">
          {previewClientName
            ? `Previewing as ${decodeURIComponent(previewClientName)}`
            : 'Previewing as Client'}
        </span>
        <button
          onClick={handleExitPreview}
          className="ml-1 p-1 rounded hover:bg-blue-200 focus:outline-none"
          aria-label="Exit client preview"
          type="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 4L12 12M12 4L4 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    );
  };

  const getMissingRequirements = () => {
    const missing = [];
    if (!pendingPlatform) missing.push('Platform');
    if (pendingAccountIds.length === 0) missing.push('Account');
    if (pendingCampaignIds.length === 0) missing.push('Campaign');
    if (!pendingDateRange.start) missing.push('Start date');
    if (!pendingDateRange.end) missing.push('End date');
    return missing.join(', ');
  };

  return (
    <>
      <div className="p-6 bg-white border-b border-gray-400 overflow-x-clip">
        <TemplateMenubar hideBack={isClientView} />
        <div className="flex items-center gap-4 relative -top-[43px] lg:-top-[50px] left-20 w-[50%]">
          <h1 className="text-xl lg:text-3xl font-light">
            Campaign Reporter
          </h1>
          <span className="hidden lg:inline-flex">
            <PreviewBanner />
          </span>
        </div>
        <button
          onClick={handleDownload}
          disabled={isGenerateDisabled || isDownloading}
          className={`hidden sm:flex absolute right-24 top-9 px-4 py-2 rounded-lg border border-gray-400 font-medium items-center gap-2 ${
            isGenerateDisabled || isDownloading
              ? 'opacity-50 cursor-not-allowed bg-gray-200'
              : 'bg-white hover:bg-gray-200'
          }`}
        >
          {isDownloading ? (
            <Loader className="size-4 animate-spin" />
          ) : (
            <Download className="size-4 invert" />
          )}
          {isDownloading ? 'Downloading...' : 'Download CSV'}
        </button>
        <div className="lg:px-12 -mt-4 flex flex-wrap gap-4">
          <div className="lg:hidden w-full mb-2">
            <PreviewBanner />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-600 mb-1 ml-1">
              Platform
            </label>
            {isFetchingPlatforms ? (
              <Skeleton className="w-full h-[38px]" />
            ) : (
              <select
                className="w-full rounded-lg p-2 border border-gray-400 focus:outline-none font-medium"
                value={pendingPlatform}
                onChange={(e) => {
                  setPendingPlatform(e.target.value);
                  setPendingAccountIds([]);
                  setPendingCampaignIds([]);
                  setUserClearedCampaigns(false);
                }}
              >
                {platforms.map((platform) => (
                  <option
                    key={platform}
                    value={platform}
                  >
                    {platform.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-600 mb-1 ml-1">
              Accounts
            </label>
            {isFetchingAccounts ? (
              <Skeleton className="w-full h-[38px]" />
            ) : (
              <>
                <input
                  type="text"
                  placeholder={pendingAccountIds.length === 0 ? "🔍 Search Accounts..." :
                    pendingAccountIds.length === accounts.length && pendingAccountIds.length > 1 ? "All Accounts" :
                      accounts.filter((account: AccountType) => pendingAccountIds.includes(account.id)).map((account: AccountType) => account.name).join(', ')}
                  className={`w-full rounded-lg p-2 border border-gray-400 mb-2 peer focus:placeholder-transparent ${pendingAccountIds.length > 0 ? 'font-medium placeholder-black' : ''}`}
                  value={searchAccount}
                  onFocus={() => setAccountDropdownVisible(true)}
                  onChange={(e) => {
                    setSearchAccount(e.target.value);
                    setAccountDropdownVisible(true);
                  }}
                />
                {isAccountDropdownVisible && (
                  <div
                    ref={accountDropdownRef}
                    className="absolute z-10 bg-white shadow-lg p-1 max-h-[200px] overflow-y-auto border border-gray-300 rounded-lg w-full"
                  >
                    <div className="flex items-center justify-evenly p-2 border-b border-gray-200">
                      <button
                        onClick={() => {
                          setPendingAccountIds(accounts.map(account => account.id));
                          setPendingCampaignIds([]);
                          setUserClearedCampaigns(false);
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Select all
                      </button>
                      <button
                        onClick={() => {
                          setPendingAccountIds([]);
                          setPendingCampaignIds([]);
                          setUserClearedCampaigns(false);
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Select none
                      </button>
                    </div>
                    {filteredAccounts.length === 0 ? (
                      <div className="flex items-center px-2 py-1 text-gray-500">
                        No accounts found.
                      </div>
                    ) : (
                      filteredAccounts.map((account: AccountType) => (
                        <div
                          key={account.id}
                          className="flex items-center px-2 py-1 hover:bg-gray-200"
                        >
                          <label className="flex items-center w-full cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pendingAccountIds.includes(account.id)}
                              onChange={() => {
                                setPendingAccountIds(prev =>
                                  prev.includes(account.id)
                                    ? prev.filter(id => id !== account.id)
                                    : [...prev, account.id]
                                );
                                setPendingCampaignIds([]);
                                setUserClearedCampaigns(false);
                              }}
                              className="mr-2 cursor-pointer"
                            />
                            <span className="flex-1">
                              {account.name}
                            </span>
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-600 mb-1 ml-1">
              Campaigns
            </label>
            {isFetchingAccounts || isFetchingCampaigns ? (
              <Skeleton className="w-full h-[38px]" />
            ) : (
              <>
                <input
                  type="text"
                  placeholder={pendingCampaignIds.length === 0 ? "🔍 Search Campaigns..." :
                    pendingCampaignIds.length === campaigns.length && pendingCampaignIds.length > 1 ? "All Campaigns" :
                      campaigns.filter((campaign: CampaignType) => pendingCampaignIds.includes(campaign.id)).map((campaign: CampaignType) => campaign.name).join(', ')}
                  className={`w-full rounded-lg p-2 border border-gray-400 mb-2 focus:outline-none peer focus:placeholder-transparent ${pendingAccountIds.length === 0 ? 'opacity-40 cursor-not-allowed' : ''} ${pendingCampaignIds.length > 0 ? 'font-medium placeholder-black' : ''}`}
                  value={searchCampaign}
                  onFocus={() => setCampaignDropdownVisible(true)}
                  onChange={(e) => {
                    setSearchCampaign(e.target.value);
                    setCampaignDropdownVisible(true);
                  }}
                  disabled={pendingAccountIds.length === 0}
                />
                {isCampaignDropdownVisible && (
                  <div
                    ref={campaignDropdownRef}
                    className="absolute z-10 bg-white shadow-lg p-1 max-h-[200px] overflow-y-auto border border-gray-300 rounded-lg w-full"
                  >
                    <div className="flex items-center justify-evenly p-2 border-b border-gray-200">
                      <button
                        onClick={() => {
                          setPendingCampaignIds(campaigns.map(campaign => campaign.id));
                          setUserClearedCampaigns(false);
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Select all
                      </button>
                      <button
                        onClick={() => {
                          setPendingCampaignIds([]);
                          setUserClearedCampaigns(true);
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Select none
                      </button>
                    </div>
                    {filteredCampaigns.length === 0 ? (
                      <div className="flex items-center px-2 py-1 text-gray-500">
                        No campaigns found.
                      </div>
                    ) : (
                      filteredCampaigns.map((campaign: CampaignType) => (
                        <div
                          key={campaign.id}
                          className="flex items-center px-2 py-1 hover:bg-gray-200"
                        >
                          <label className="flex items-center w-full cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pendingCampaignIds.includes(campaign.id)}
                              onChange={() => {
                                setPendingCampaignIds(prev => {
                                  const newIds = prev.includes(campaign.id)
                                    ? prev.filter(id => id !== campaign.id)
                                    : [...prev, campaign.id];

                                  // Reset the flag if user is selecting campaigns
                                  if (!prev.includes(campaign.id)) {
                                    setUserClearedCampaigns(false);
                                  }

                                  return newIds;
                                });
                              }}
                              className="mr-2 cursor-pointer"
                              disabled={pendingAccountIds.length === 0}
                            />
                            <span className="flex-1">
                              {campaign.name}
                            </span>
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex-2 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-600 mb-1 ml-1">
              Date Range
            </label>
            <div className="flex flex-wrap gap-4">
              <input
                type="date"
                value={pendingDateRange.start}
                max={getMaxDate()}
                className="rounded-lg p-2 border border-gray-400 focus:outline-none font-medium flex-1"
                onChange={(e) => setPendingDateRange(prev => ({
                  start: e.target.value,
                  end: prev.end,
                }))}
              />
              <input
                type="date"
                value={pendingDateRange.end}
                max={getMaxDate()}
                className="rounded-lg p-2 border border-gray-400 focus:outline-none font-medium flex-1"
                onChange={(e) => setPendingDateRange(prev => ({
                  start: prev.start,
                  end: e.target.value,
                }))}
              />
            </div>
          </div>
          <button
            onClick={(e) => {
              if (isGenerateDisabled) {
                e.preventDefault();
                return;
              }
              handleSubmit();
            }}
            className={`flex-1 xl:flex-none h-[43px] px-6 mt-[24px] rounded-lg font-medium items-center justify-center gap-2 relative group ${
              isGenerateDisabled
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            Update
            {isGenerateDisabled && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                {`${getMissingRequirements()} is required`}
              </div>
            )}
          </button>
        </div>
      </div>
      <div className="flex flex-col pt-6 pb-8 px-4 lg:px-16 gap-8">
        <div className="flex flex-col grow justify-start">
          {(!reportMetrics?.current ||
            !timeSeriesData.length ||
            !performanceData?.length) && !isLoadingMetrics && !isLoadingTimeSeries && !isLoadingPerformanceData && !isInitialLoad ? (
              <div className="flex items-center justify-center h-[200px] text-gray-500 text-lg">
                No data available. Please modify your selection criteria and try again.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 auto-rows-max">
                  {shouldShowMetric('clicks') && (
                    <MetricCard
                      title={metricCardClicksTitle()}
                      currentValue={metricCardClicksValue(false)}
                      difference={metricCardClicksValue(true)}
                      isLoading={isLoadingMetrics}
                    />
                  )}
                  {shouldShowMetric('impressions') && (
                    <MetricCard
                      title="Impressions"
                      currentValue={reportMetrics?.current.impressions || 0}
                      difference={reportMetrics?.dif.impressions || 0}
                      isLoading={isLoadingMetrics}
                    />
                  )}
                  {/* {shouldShowMetric('reach') && (
                    <MetricCard
                      title="Reach"
                      currentValue={reportMetrics?.current.reach || 0}
                      difference={reportMetrics?.dif.reach || 0}
                      isLoading={isLoadingMetrics}
                    />
                  )} */}
                  {shouldShowMetric('conversions') && (
                    <MetricCard
                      title="Conversions"
                      currentValue={reportMetrics?.current.conversions || 0}
                      difference={reportMetrics?.dif.conversions || 0}
                      isLoading={isLoadingMetrics}
                    />
                  )}
                  {shouldShowMetric('ctr') && (
                    <MetricCard
                      title="CTR"
                      currentValue={reportMetrics?.current?.ctr ? reportMetrics.current.ctr : 0}
                      difference={reportMetrics?.dif?.ctr ? reportMetrics.dif.ctr : 0}
                      isLoading={isLoadingMetrics}
                      percentage
                    />
                  )}
                  {shouldShowMetric('spend') && (
                    <MetricCard
                      title="Cost"
                      currentValue={reportMetrics?.current.spend || 0}
                      difference={reportMetrics?.dif.spend || 0}
                      isLoading={isLoadingMetrics}
                      dollarValue
                    />
                  )}
                  {shouldShowMetric('cpm') && (
                    <MetricCard
                      title="CPM"
                      currentValue={reportMetrics?.current.cpm || 0}
                      difference={reportMetrics?.dif.cpm || 0}
                      isLoading={isLoadingMetrics}
                      dollarValue
                      lowerIsBetter
                    />
                  )}
                  {shouldShowMetric('cpa') && (
                    <MetricCard
                      title="CPA"
                      currentValue={reportMetrics?.current.cpa || 0}
                      difference={reportMetrics?.dif.cpa || 0}
                      isLoading={isLoadingMetrics}
                      dollarValue
                      lowerIsBetter
                    />
                  )}
                  {shouldShowMetric('video_completion_rate') && (
                    <MetricCard
                      title="Video Completion Rate"
                      currentValue={calculateVideoCompletionRate()}
                      difference={calculateVideoCompletionRateDifference()}
                      isLoading={isLoadingMetrics}
                      percentage
                    />
                  )}
                  {shouldShowMetric('audio_completion_rate') && (
                    <MetricCard
                      title="Audio Completion Rate"
                      currentValue={calculateAudioCompletionRate()}
                      difference={calculateAudioCompletionRateDifference()}
                      isLoading={isLoadingMetrics}
                      percentage
                    />
                  )}
                  {shouldShowMetric('geo') && (
                    <TableMetricCard
                      title="Top Zip Codes"
                      data={
                        geoData?.map((item) => ({
                          key: item.key,
                          impressions: item.impressions,
                        })) || []
                      }
                      columns={[
                        { key: 'key', label: 'Zip Code', selectable: true },
                        { key: 'impressions', label: 'Impressions', selectable: true },
                      ]}
                      isLoading={isLoadingGeoData}
                      sortBy="impressions"
                      sortOrder="desc"
                    />
                  )}
                  {shouldShowMetric('domain') && (
                    <TableMetricCard
                      title={platform === 'dv_360' ? 'Top Serving Sources' : 'Top Serving Domains'}
                      data={
                        domainData?.map((item) => ({
                          key: item.key,
                          impressions: item.impressions,
                        })) || []
                      }
                      columns={[
                        { key: 'key', label: platform === 'dv_360' ? 'Source' : 'Website', selectable: true },
                        { key: 'impressions', label: 'Impressions', selectable: true },
                      ]}
                      isLoading={domainLoading}
                      sortBy="impressions"
                      sortOrder="desc"
                    />
                  )}
                  {/* DV360: Duplicate card, so hide if top domains already showing, and rename both to top serving "sources" */}
                  {shouldShowMetric('app') && !(platform === 'dv_360' && shouldShowMetric('domain')) && (
                    <TableMetricCard
                      title={platform === 'dv_360' ? 'Top Serving Sources' : 'Top Serving Apps'}
                      data={
                        appData?.map((item) => ({
                          key: item.key,
                          impressions: item.impressions,
                        })) || []
                      }
                      columns={[
                        { key: 'key', label: platform === 'dv_360' ? 'Source' : 'App', selectable: true },
                        { key: 'impressions', label: 'Impressions', selectable: true },
                      ]}
                      isLoading={appLoading}
                      sortBy="impressions"
                      sortOrder="desc"
                    />
                  )}
                </div>
                {timeSeriesData.length > 0 && (
                  <div className="flex gap-4 flex-wrap mt-4">
                    {getGraphMetrics(1).length > 0 && (
                      <MetricChart
                        title={getGraphTitle(1)}
                        data={timeSeriesData}
                        metrics={getGraphMetrics(1)}
                        isLoading={isLoadingTimeSeries}
                      />
                    )}
                    {getGraphMetrics(2).length > 0 && (
                      <MetricChart
                        title={getGraphTitle(2)}
                        data={timeSeriesData}
                        metrics={getGraphMetrics(2)}
                        isLoading={isLoadingTimeSeries}
                      />
                    )}
                    {getGraphMetrics(3).length > 0 && (
                      <MetricChart
                        title={getGraphTitle(3)}
                        data={timeSeriesData}
                        metrics={getGraphMetrics(3)}
                        isLoading={isLoadingTimeSeries}
                      />
                    )}
                  </div>
                )}
                {(performanceData?.length || isLoadingTimeSeries) && (
                  <div className="mt-4">
                    <CampaignBreakdownTable
                      data={performanceData || []}
                      isLoading={isLoadingTimeSeries}
                      shouldShowMetric={shouldShowMetric}
                    />
                  </div>
                )}
              </>
            )}
        </div>
        {!isClientView && !previewClientId && (
          <div className="flex flex-wrap w-full gap-6">
            <div
              ref={aiReporterRef}
              className="flex flex-col w-full rounded-2xl bg-white border border-gray-400 scroll-mt-8"
            >
              <div className="flex flex-col lg:flex-row lg:max-h-[750px]">
                <div className="flex-1">
                  <div className="p-8 pb-2 lg:pb-8">
                    <div className="text-2xl font-light mb-4">
                      AI Reporter
                    </div>
                    <ChatInterface
                      isDisabled={isGenerateDisabled || isChatLoading}
                      onSendMessage={handleSendMessage}
                      messages={messages}
                    />
                  </div>
                </div>
                <div className="hidden lg:block w-px bg-gray-400" />
                <div className="flex-1">
                  {aiGeneratedItems.length > 0 ? (
                    <AIGeneratedItems
                      items={aiGeneratedItems}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      isLoading={isChatLoading}
                    />
                  ) : (
                    <div className="p-8 text-gray-500">
                      {isChatLoading ? (
                        <div className="animate-pulse">
                          Generating response...
                        </div>
                      ) : (
                        "Send a message to get AI insights about your campaign."
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CampaignReporterPage;
